import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  useToast,
} from "alm-react-components";
import Evaporate from "evaporate";
import { sha256 } from "js-sha256";
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ActionType } from "../../assets/constants/constants";
import { AppContext } from "../../context/AppProvider";
import { getCheckToken, md5 } from "../../lib/utils";
import { apis } from "../../services/alm";
import { ALM_URL } from "../../services/urls";
import {
  deleteAvatarImage,
  getUserData,
  userAvatarurlUpdate,
} from "../../services/user";
import ImageCropperModal from "../../components/common/imageCropperModal/ImageCropperModal";
import useAlmCorporateStatus from "../../hooks/useAlmCorporateStatus";

const Profile = () => {
  const { toast } = useToast();
  const { state, dispatch } = useContext(AppContext);
  const [img, setImg] = useState(state?.user?.avatarUrl);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const userIdImg = state?.user?.userId;
  useAlmCorporateStatus();
  // Critical: Use refs to maintain stable references across re-renders
  const evaporateInstanceRef = useRef<any>(null);
  const awsCredentialsRef = useRef<any>(null);
  const isInitializedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const UPLOAD_SINGER_URL = `${ALM_URL}/uploadSigner`;

  const uploadProfile = useCallback(
    (imageUrl: string) => {
      dispatch({ type: ActionType.SET_LOADING, payload: true });

      apis
        .uploadImage(imageUrl)
        .then(async (response) => {
          await userAvatarurlUpdate();
          const AvatarUrl = response?.data?.attributes?.avatarUrl;
          const fileName = AvatarUrl.split("/").pop();
          setImg(AvatarUrl);
          setIsDeleteDisabled(false);
          dispatch({ type: "PROFILE_CHANGE", payload: AvatarUrl });
          setIsLoading(false);

          if (fileName === "default_user_avatar.svg") {
            toast({
              description: t("ErrorType.errorduringUploadImage"),
              color: "white",
              variant: "error",
            });
          } else {
            toast({
              description: (
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("Common.profileUploaded"),
                  }}
                />
              ),
              variant: "success",
            });
          }
          dispatch({ type: ActionType.SET_LOADING, payload: false });
        })
        .catch((error) => {
          console.error("🔥 Upload profile error:", error);
          toast({
            description: t(`ErrorType.errorUploadImage`),
            variant: "error",
          });
          setIsLoading(false);
          dispatch({ type: ActionType.SET_LOADING, payload: false });
        });
    },
    [dispatch, toast, t]
  ); // Removed isLoading dependency

  const handleDeleteAvatar = async () => {
    setIsLoading(true);
    try {
      await deleteAvatarImage();
      const response = await getUserData();
      const defaultAvatarUrl = response?.attributes?.avatarUrl;

      setImg(defaultAvatarUrl);
      setIsDeleteDisabled(true);
      dispatch({ type: "PROFILE_CHANGE", payload: defaultAvatarUrl });
      toast({
        description: (
          <span
            dangerouslySetInnerHTML={{ __html: t("Common.avatarDeleted") }}
          />
        ),
        variant: "success",
      });
    } catch (error) {
      console.error("Delete avatar error:", error);
      toast({
        description: t(`ErrorType.errorDeleteImage`),
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // File selection handler - stable reference
  const handleFileSelection = useCallback(
    (file: File) => {
      const fileExtension = file?.name.split(".").pop();
      const fileSize = file.size;
      // File validation
      if (
        !fileExtension ||
        ["jpg", "png", "jpeg"].indexOf(fileExtension.toLowerCase()) === -1
      ) {
        toast({
          description: t(`Common.invalidFormat`),
          variant: "error",
        });
        return;
      }

      if (Number(Math.round(+fileSize / 1024) / 1000) > 8) {
        toast({
          description: t(`Common.fileSize`),
          variant: "error",
        });
        return;
      }
      setSelectedFile(file);
      setShowCropModal(true);
    },
    [toast, t]
  ); // Removed selectedFile and showCropModal dependencies

  // Initialize Evaporate instance - called only once
  const initializeUploadSystem = useCallback(async () => {
    if (isInitializedRef.current) {
      return;
    }

    try {
      // Fetch AWS credentials
      const response = await apis.getUploadInfo();
      const awsInfo = response?.data;
      if (!awsInfo) {
        throw new Error("No AWS credentials received");
      }

      // Store credentials in ref
      const awsRegionUrl =
        awsInfo?.region?.indexOf("us-") === 0
          ? ".s3.amazonaws.com/"
          : ".s3." + awsInfo?.region + ".amazonaws.com/";

      const awsUrl =
        window?.location?.host?.indexOf("9010") === -1
          ? "https://" + awsInfo?.bucket + awsRegionUrl + awsInfo?.key
          : import.meta.env.VITE_APP_URL + awsInfo?.bucket + "/" + awsInfo?.key;

      awsCredentialsRef.current = {
        ...awsInfo,
        awsUrl: awsUrl,
      };


      // Create Evaporate config
      const config = {
        aws_key: awsInfo?.awsKey,
        bucket: awsInfo?.bucket,
        awsRegion: awsInfo?.region,
        cloudfront: true,
        xhrWithCredentials: true,
        signerUrl: UPLOAD_SINGER_URL,
        awsSignatureVersion: "4" as const,
        computeContentMd5: true,
        signHeaders: {
          Authorization: "oauth " + getCheckToken(),
          "Content-Type": "application/vnd.api+json",
        },
        aws_url: awsUrl,
        cryptoMd5Method: function (data: any) {
          return md5(data);
        },
        cryptoHexEncodedHash256: function (data: any) {
          return sha256(data);
        },
      };
      // Create Evaporate instance
      const evaporateInstance = await Evaporate.create(config);
      evaporateInstanceRef.current = evaporateInstance;
      isInitializedRef.current = true;
    } catch (error) {
      console.error("🔥 Failed to initialize upload system:", error);
      toast({
        description: t(`ErrorType.errorInitUpload`),
        variant: "error",
      });
    }
  }, [toast, t]);

  // Handle cropped image upload
  const handleCroppedImage = useCallback(
    async (canvas: HTMLCanvasElement) => {
      if (!selectedFile) {
        toast({
          description: t(`ErrorType.errorCropImage`),
          variant: "error",
        });
        return;
      }

      // Ensure upload system is initialized
      if (
        !isInitializedRef.current ||
        !evaporateInstanceRef.current ||
        !awsCredentialsRef.current
      ) {
        await initializeUploadSystem();

        // Double check after initialization
        if (!evaporateInstanceRef.current || !awsCredentialsRef.current) {
          toast({
            description: t(`ErrorType.errorInitUpload`),
            variant: "error",
          });
          return;
        }
      }
      setIsLoading(true);
      dispatch({ type: ActionType.SET_LOADING, payload: true });

      const fileExtension =
        selectedFile.name.split(".").pop()?.toLowerCase() || "jpg";

      // Determine MIME type
      let mimeType = "image/jpeg";
      if (fileExtension === "png") {
        mimeType = "image/png";
      }

      // Convert canvas to blob
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            toast({
              description: t(`ErrorType.errorCropImage`),
              variant: "error",
            });
            setIsLoading(false);
            dispatch({ type: ActionType.SET_LOADING, payload: false });
            return;
          }

          try {
            const timestamp = new Date().getTime();
            const filename = `profile_${userIdImg}_${timestamp}.${fileExtension}`;

            // Create file from blob
            const croppedFile = new File([blob], filename, {
              type: mimeType,
              lastModified: timestamp,
            });

            // Upload to S3 using current evaporate instance
            const result = await evaporateInstanceRef.current.add({
              name: filename,
              file: croppedFile,
              contentType: croppedFile.type,
            });

            // Create final image URL
            const imgUrl = `${awsCredentialsRef.current.awsUrl}/${result}`;

            // Upload profile
            uploadProfile(imgUrl);

            // Clean up
            setShowCropModal(false);
            setSelectedFile(null);
          } catch (error) {
            console.error("🔥 Error during S3 upload:", error);
            toast({
              description: t(`ErrorType.errorUploadImage`),
              variant: "error",
            });
            setIsLoading(false);
            dispatch({ type: ActionType.SET_LOADING, payload: false });
          }
        },
        mimeType,
        0.95
      );
    },
    [
      selectedFile,
      userIdImg,
      uploadProfile,
      dispatch,
      toast,
      t,
      initializeUploadSystem,
    ]
  );

  const handleCancelCrop = useCallback(() => {
    setShowCropModal(false);
    setSelectedFile(null);
  }, []);

  // File input change handler
  const handleFileInputChange = useCallback(
    (evt: Event) => {
      const target = evt.target as HTMLInputElement;
      if (!target.files || target.files.length === 0) {
        console.warn("🔥 No file selected");
        return;
      }
      const file = target.files[0];
      handleFileSelection(file);

      // Reset input to allow same file selection
      target.value = "";
    },
    [handleFileSelection]
  );

  // Initialize upload system on mount
  useEffect(() => {
    initializeUploadSystem();
  }, [initializeUploadSystem]);

  // Set up file input handler
  useEffect(() => {
    const fileInput = document.getElementById("files") as HTMLInputElement;
    if (fileInput) {
      // Check if it's the same element
      fileInputRef.current = fileInput;
      // Remove any existing listeners first
      fileInput.removeEventListener("change", handleFileInputChange);

      // Add the new listener
      fileInput.addEventListener("change", handleFileInputChange);
      return () => {
        if (fileInput && fileInput.removeEventListener) {
          fileInput.removeEventListener("change", handleFileInputChange);
        }
      };
    } else {
      console.error("🔥 useEffect: File input element not found!");
    }
  }, [handleFileInputChange]);

  // Update avatar state when user data changes (but don't reinitialize upload system)
  useEffect(() => {
    if (state.user?.avatarUrl) {
      setImg(state.user?.avatarUrl);
      const fileName = state?.user?.avatarUrl.split("/").pop();
      setIsDeleteDisabled(fileName === "default_user_avatar.svg");
    }
  }, [state.user]);

  return (
    <>
      {isLoading ? (
        t(`Common.updating`)
      ) : (
        <>
          <ImageCropperModal
            selectedFile={selectedFile}
            onCrop={handleCroppedImage}
            onCancel={handleCancelCrop}
            isVisible={showCropModal}
          />
          <Avatar className="relative w-[138px] h-[138px] overflow-hidden bg-gray-100 rounded-full dark:bg-gray-600">
            <AvatarImage
              className="absolute text-white"
              src={img}
              alt="profile"
            />
            <AvatarFallback>CN</AvatarFallback>
          </Avatar>
          <div className="flex justify-between gap-2">
            <label
              htmlFor="files"
              className="flex flex-row items-center text-sm font-normal px-3 py-2 h-auto border rounded-[6px]"
              role="button"
            >
              {t("Common.upload")}
            </label>
            <button
              className="flex flex-row items-center text-sm font-normal px-3 py-2 h-auto border rounded-[6px] disabled:pointer-events-none disabled:opacity-50"
              onClick={handleDeleteAvatar}
              disabled={isDeleteDisabled}
              title="Delete Avatar"
            >
              {t("Common.delete")}
            </button>
          </div>
          <input
            type="file"
            id="files"
            accept=".jpg,.jpeg,.png"
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFileSelection(e.target.files[0]);
                e.target.value = "";
              }
            }}
          />
        </>
      )}
    </>
  );
};

export default Profile;
